import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  SkeletonRows,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import {
  adminUserBidRowSchema,
  adminUserDepositRowSchema,
  adminUserPaymentRowSchema,
  depositReconcileResultSchema,
  depositRefundResultSchema,
  paginatedSchema,
  paymentReconcileResultSchema,
  userSummarySchema,
} from '@bia-auction/shared-schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Inbox } from 'lucide-react';
import { useState } from 'react';
import { Empty, ErrorMsg, Loading } from '../components/Feedback';
import { Pagination } from '../components/Pagination';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime, formatKurus } from '../lib/format';
import { bidKindLabel, depositStateLabel, lotStateLabel, paymentStateLabel, roleLabel } from '../lib/labels';
import { can } from '../lib/roles';

const PAGE = 20;

const bidsPageSchema = paginatedSchema(adminUserBidRowSchema);
const depositsPageSchema = paginatedSchema(adminUserDepositRowSchema);
const paymentsPageSchema = paginatedSchema(adminUserPaymentRowSchema);

// Mirrors the server's STUCK_PRE3D_AFTER_MS (2× the 15-min 3DS TTL): a PRE3D payment whose
// WAITING→PRE3D claim is older than this lost its callback — the reconcile/mark-failed
// actions enable on it.
const STUCK_PRE3D_AFTER_MS = 30 * 60_000;

/** A `PRE3D` payment whose claim (`pre3dAt`, createdAt fallback — same as the server
 * predicate) is old enough that its 3DS callback is considered lost (stuck). */
function isStuckPayment(p: { state: string; createdAt: number; pre3dAt: number | null }): boolean {
  return p.state === 'PRE3D' && (p.pre3dAt ?? p.createdAt) <= Date.now() - STUCK_PRE3D_AFTER_MS;
}

/**
 * A terminal deposit whose PAYMENT is still SUCCESSFUL is an **ambiguously-failed refund**
 * (the bank call threw/returned no answer): money may or may not have moved, and the slot is
 * locked until manual reconciliation. It must not render like a completed void — another
 * operator would wrongly conclude the money was returned.
 */
function isReconcilePending(d: { state: string; paymentState: string }): boolean {
  return (
    (d.state === 'VOIDED' || d.state === 'REFUNDED') && d.paymentState === 'SUCCESSFUL'
  );
}

function depositStateVariant(state: string) {
  if (state === 'HELD') return 'success' as const;
  if (state === 'REFUNDED' || state === 'VOIDED') return 'secondary' as const;
  return 'outline' as const;
}

/** The pending confirm-dialog action (replaces the four native confirm() calls). */
type PendingAction =
  | { kind: 'refund'; id: string; description: string }
  | { kind: 'reconcile'; id: string; description: string }
  | { kind: 'payReconcile'; id: string; description: string }
  | { kind: 'markFailed'; id: string; description: string };

/**
 * Bidder profile (M10): one user's identity card + cross-sale bid history + deposits, the
 * latter carrying the per-row **Refund** action (`POST /admin/deposits/:id/refund` — ADMIN/
 * DEVELOPER only, enabled on HELD). Deep-links into the audit viewer filtered to this user.
 */
export function UserDetailPage() {
  const { userId = '' } = useParams({ strict: false });
  const { state } = useAuth();
  const qc = useQueryClient();
  const [bidsOffset, setBidsOffset] = useState(0);
  const [depositsOffset, setDepositsOffset] = useState(0);
  const [paymentsOffset, setPaymentsOffset] = useState(0);
  const [pending, setPending] = useState<PendingAction | null>(null);

  const user = useQuery({
    queryKey: ['user', userId],
    queryFn: () => api(`/admin/users/${userId}`, {}, userSummarySchema),
  });
  const bids = useQuery({
    queryKey: ['user-bids', userId, bidsOffset],
    queryFn: () =>
      api(`/admin/users/${userId}/bids?limit=${PAGE}&offset=${bidsOffset}`, {}, bidsPageSchema),
  });
  const deposits = useQuery({
    queryKey: ['user-deposits', userId, depositsOffset],
    queryFn: () =>
      api(
        `/admin/users/${userId}/deposits?limit=${PAGE}&offset=${depositsOffset}`,
        {},
        depositsPageSchema,
      ),
  });
  const payments = useQuery({
    queryKey: ['user-payments', userId, paymentsOffset],
    queryFn: () =>
      api(
        `/admin/users/${userId}/payments?limit=${PAGE}&offset=${paymentsOffset}`,
        {},
        paymentsPageSchema,
      ),
  });

  const refund = useMutation({
    mutationFn: (depositId: string) =>
      api(`/admin/deposits/${depositId}/refund`, { method: 'POST' }, depositRefundResultSchema),
    onSuccess: (r) => {
      toast(t(r.op === 'void' ? 'deposits.refund.voided' : 'deposits.refund.refunded'), 'success');
      void qc.invalidateQueries({ queryKey: ['user-deposits', userId] });
    },
    onError: (e) => {
      // Localize the coded outcome (the envelope's stable `error` label) — the server's
      // message strings are English-only, and these are important operator-facing states.
      const code = e instanceof ApiError ? e.code : undefined;
      const key =
        code === 'RefundBlockedTopBidder'
          ? 'deposits.refund.blockedTopBidder'
          : code === 'RefundDeclined'
            ? 'deposits.refund.declined'
            : code === 'RefundAmbiguous'
              ? 'deposits.refund.ambiguous'
              : code === 'RefundInProgress'
                ? 'deposits.refund.inProgress'
                : code === 'RefundNotRefundable'
                  ? 'deposits.refund.notRefundable'
                  : 'deposits.refund.failed';
      toast(t(key), 'error');
      void qc.invalidateQueries({ queryKey: ['user-deposits', userId] });
    },
  });

  // Status-inquiry reconciliation of an ambiguously-failed refund (ADR 0011 amendment). The
  // inquiry is read-only at the bank, so retrying a failed reconcile is always safe.
  const reconcile = useMutation({
    mutationFn: (depositId: string) =>
      api(`/admin/deposits/${depositId}/reconcile`, { method: 'POST' }, depositReconcileResultSchema),
    onSuccess: (r) => {
      const key =
        r.op === 'reverted'
          ? 'deposits.reconcile.reverted'
          : r.op === 'void'
            ? 'deposits.reconcile.confirmedVoided'
            : 'deposits.reconcile.confirmedRefunded';
      toast(t(key), 'success');
      void qc.invalidateQueries({ queryKey: ['user-deposits', userId] });
    },
    onError: (e) => {
      const code = e instanceof ApiError ? e.code : undefined;
      const key =
        code === 'ReconcileAmbiguous'
          ? 'deposits.reconcile.ambiguous'
          : code === 'ReconcileNotPending'
            ? 'deposits.reconcile.notPending'
            : code === 'ReconcileInProgress'
              ? 'deposits.reconcile.inProgress'
              : code === 'ReconcileCooldown'
                ? 'deposits.reconcile.cooldown'
                : 'deposits.reconcile.failed';
      toast(t(key), 'error');
      void qc.invalidateQueries({ queryKey: ['user-deposits', userId] });
    },
  });

  // Stuck-PRE3D resolution (ADR 0011 amendment): reconcile asks the bank (read-only, safe to
  // repeat); mark-failed is the explicit human decision for a permanently-UNKNOWN order.
  const paymentReconcile = useMutation({
    mutationFn: (paymentId: string) =>
      api(`/admin/payments/${paymentId}/reconcile`, { method: 'POST' }, paymentReconcileResultSchema),
    onSuccess: (r) => {
      toast(t(r.op === 'confirmed' ? 'payments.reconcile.confirmed' : 'payments.reconcile.returned'), 'success');
      void qc.invalidateQueries({ queryKey: ['user-payments', userId] });
      // A confirmed deposit-kind payment also (re)creates a HELD deposit row; a confirmed
      // membership changes the user's roles — refresh both surfaces.
      void qc.invalidateQueries({ queryKey: ['user-deposits', userId] });
      void qc.invalidateQueries({ queryKey: ['user', userId] });
    },
    onError: (e) => {
      const code = e instanceof ApiError ? e.code : undefined;
      const key =
        code === 'ReconcileAmbiguous'
          ? 'payments.reconcile.ambiguous'
          : code === 'ReconcileNotPending'
            ? 'payments.reconcile.notPending'
            : 'payments.reconcile.failed';
      toast(t(key), 'error');
      void qc.invalidateQueries({ queryKey: ['user-payments', userId] });
    },
  });
  const markFailed = useMutation({
    mutationFn: (paymentId: string) =>
      api(`/admin/payments/${paymentId}/mark-failed`, { method: 'POST' }, paymentReconcileResultSchema),
    onSuccess: () => {
      toast(t('payments.markFailed.done'), 'success');
      void qc.invalidateQueries({ queryKey: ['user-payments', userId] });
    },
    onError: (e) => {
      const code = e instanceof ApiError ? e.code : undefined;
      toast(t(code === 'ReconcileNotPending' ? 'payments.reconcile.notPending' : 'payments.reconcile.failed'), 'error');
      void qc.invalidateQueries({ queryKey: ['user-payments', userId] });
    },
  });

  const anyBusy =
    refund.isPending || reconcile.isPending || paymentReconcile.isPending || markFailed.isPending;

  const runPending = () => {
    if (!pending) return;
    if (pending.kind === 'refund') refund.mutate(pending.id);
    else if (pending.kind === 'reconcile') reconcile.mutate(pending.id);
    else if (pending.kind === 'payReconcile') paymentReconcile.mutate(pending.id);
    else markFailed.mutate(pending.id);
    setPending(null);
  };

  if (user.isLoading) return <Loading />;
  if (user.isError) return <ErrorMsg error={user.error} />;
  const u = user.data;
  if (!u) return <Empty label={t('users.detail.notFound')} />;

  return (
    <div className="space-y-6">
      <PageHeader
        title={[u.nameFirst, u.nameLast].filter(Boolean).join(' ') || u.email}
        subline={
          <span className="flex flex-wrap items-center gap-1">
            {u.email}
            {u.roles.map((r) => (
              <Badge key={r} variant="secondary">
                {roleLabel(r)}
              </Badge>
            ))}
          </span>
        }
      >
        {can(state.roles, 'auditView') && (
          <Button asChild variant="ghost" size="sm">
            <Link to="/audit" search={{ actorId: u.id }}>
              {t('users.detail.viewAudit')}
            </Link>
          </Button>
        )}
      </PageHeader>

      {/* The three stacked tables become tabs (redesign-admin.md §6); the pagination
          envelopes' totals double as the summary counts. */}
      <Tabs defaultValue="deposits">
        <TabsList>
          <TabsTrigger value="deposits">
            {t('users.detail.deposits')} ({deposits.data?.total ?? 0})
          </TabsTrigger>
          <TabsTrigger value="payments">
            {t('users.detail.payments')} ({payments.data?.total ?? 0})
          </TabsTrigger>
          <TabsTrigger value="bids">
            {t('users.detail.bids')} ({bids.data?.total ?? 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="deposits">
          {deposits.isLoading ? (
            <SkeletonRows rows={4} />
          ) : deposits.isError ? (
            <ErrorMsg error={deposits.error} />
          ) : deposits.data && deposits.data.items.length > 0 ? (
            <div className="space-y-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('users.detail.col.sale')}</TableHead>
                    <TableHead>{t('users.detail.col.plate')}</TableHead>
                    <TableHead>{t('users.detail.col.amount')}</TableHead>
                    <TableHead>{t('users.detail.col.state')}</TableHead>
                    <TableHead>{t('users.detail.col.date')}</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deposits.data.items.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell className="font-mono text-xs">{d.saleCode}</TableCell>
                      <TableCell>{d.plateSnapshot ?? '—'}</TableCell>
                      <TableCell className="tabular-nums">{formatKurus(d.amount)}</TableCell>
                      <TableCell>
                        {isReconcilePending(d) ? (
                          <div className="space-y-0.5">
                            <StatusBadge
                              variant="warning"
                              title={t('deposits.state.reconcileHint')}
                            >
                              {t('deposits.state.reconcile')}
                            </StatusBadge>
                            {/* The bank order id — the reconciliation handle: without it the
                                operator would have to search the bank portal by amount/user. */}
                            {d.orderId && (
                              <div className="font-mono text-xs text-muted-foreground">
                                {t('deposits.orderIdLabel')}: {d.orderId}
                              </div>
                            )}
                          </div>
                        ) : (
                          <StatusBadge variant={depositStateVariant(d.state)}>
                            {depositStateLabel(d.state)}
                          </StatusBadge>
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums">{formatDateTime(d.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        {can(state.roles, 'depositRefund') && d.state === 'HELD' && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={anyBusy}
                            onClick={() =>
                              setPending({
                                kind: 'refund',
                                id: d.id,
                                description: t('deposits.refund.confirm', {
                                  amount: formatKurus(d.amount),
                                  sale: d.saleCode,
                                }),
                              })
                            }
                          >
                            {t('deposits.refund.action')}
                          </Button>
                        )}
                        {can(state.roles, 'depositRefund') && isReconcilePending(d) && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={anyBusy}
                            onClick={() =>
                              setPending({
                                kind: 'reconcile',
                                id: d.id,
                                description: t('deposits.reconcile.confirm'),
                              })
                            }
                          >
                            {t('deposits.reconcile.action')}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                offset={depositsOffset}
                limit={PAGE}
                total={deposits.data.total}
                onOffsetChange={setDepositsOffset}
              />
            </div>
          ) : (
            <EmptyState icon={Inbox} title={t('users.detail.noDeposits')} />
          )}
        </TabsContent>

        <TabsContent value="payments">
          {payments.isLoading ? (
            <SkeletonRows rows={4} />
          ) : payments.isError ? (
            <ErrorMsg error={payments.error} />
          ) : payments.data && payments.data.items.length > 0 ? (
            <div className="space-y-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('users.detail.col.order')}</TableHead>
                    <TableHead>{t('users.detail.col.kind')}</TableHead>
                    <TableHead>{t('users.detail.col.amount')}</TableHead>
                    <TableHead>{t('users.detail.col.state')}</TableHead>
                    <TableHead>{t('users.detail.col.date')}</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.data.items.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-mono text-xs">{p.orderId}</TableCell>
                      <TableCell>
                        {t(p.kind === 'deposit' ? 'payments.kind.deposit' : 'payments.kind.membership')}
                      </TableCell>
                      <TableCell className="tabular-nums">{formatKurus(p.amount, p.currency)}</TableCell>
                      <TableCell>
                        {isStuckPayment(p) ? (
                          <StatusBadge variant="warning" title={t('payments.state.stuckHint')}>
                            {t('payments.state.stuck')}
                          </StatusBadge>
                        ) : (
                          <StatusBadge variant={p.state === 'SUCCESSFUL' ? 'success' : 'secondary'}>
                            {paymentStateLabel(p.state)}
                          </StatusBadge>
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums">{formatDateTime(p.createdAt)}</TableCell>
                      <TableCell className="space-x-2 text-right">
                        {can(state.roles, 'depositRefund') && isStuckPayment(p) && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={anyBusy}
                              onClick={() =>
                                setPending({
                                  kind: 'payReconcile',
                                  id: p.id,
                                  description: t('payments.reconcile.confirm'),
                                })
                              }
                            >
                              {t('payments.reconcile.action')}
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              disabled={anyBusy}
                              onClick={() =>
                                setPending({
                                  kind: 'markFailed',
                                  id: p.id,
                                  description: t('payments.markFailed.confirm'),
                                })
                              }
                            >
                              {t('payments.markFailed.action')}
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                offset={paymentsOffset}
                limit={PAGE}
                total={payments.data.total}
                onOffsetChange={setPaymentsOffset}
              />
            </div>
          ) : (
            <EmptyState icon={Inbox} title={t('users.detail.noPayments')} />
          )}
        </TabsContent>

        <TabsContent value="bids">
          {bids.isLoading ? (
            <SkeletonRows rows={4} />
          ) : bids.isError ? (
            <ErrorMsg error={bids.error} />
          ) : bids.data && bids.data.items.length > 0 ? (
            <div className="space-y-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('users.detail.col.sale')}</TableHead>
                    <TableHead>{t('users.detail.col.plate')}</TableHead>
                    <TableHead>{t('users.detail.col.amount')}</TableHead>
                    <TableHead>{t('users.detail.col.kind')}</TableHead>
                    <TableHead>{t('users.detail.col.lotState')}</TableHead>
                    <TableHead>{t('users.detail.col.date')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bids.data.items.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell className="font-mono text-xs">
                        {can(state.roles, 'salesView') ? (
                          <Link
                            to="/sales/$saleId"
                            params={{ saleId: b.saleId }}
                            className="text-tx-brand hover:underline"
                          >
                            {b.saleCode}
                          </Link>
                        ) : (
                          b.saleCode
                        )}
                      </TableCell>
                      <TableCell>{b.plateSnapshot ?? '—'}</TableCell>
                      <TableCell className="tabular-nums">{formatKurus(b.amount, b.currency)}</TableCell>
                      <TableCell>{bidKindLabel(b.kind)}</TableCell>
                      <TableCell>{lotStateLabel(b.lotState)}</TableCell>
                      <TableCell className="tabular-nums">{formatDateTime(b.placedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                offset={bidsOffset}
                limit={PAGE}
                total={bids.data.total}
                onOffsetChange={setBidsOffset}
              />
            </div>
          ) : (
            <EmptyState icon={Inbox} title={t('users.detail.noBids')} />
          )}
        </TabsContent>
      </Tabs>

      {/* One shared confirm dialog for the four money-movement actions (spec §0 D1);
          mark-failed is the only destructive one. */}
      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(o) => !o && setPending(null)}
        title={
          pending?.kind === 'refund'
            ? t('deposits.refund.action')
            : pending?.kind === 'reconcile' || pending?.kind === 'payReconcile'
              ? t('deposits.reconcile.action')
              : t('payments.markFailed.action')
        }
        description={pending?.description ?? ''}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        destructive={pending?.kind === 'markFailed'}
        busy={anyBusy}
        onConfirm={runPending}
      />
    </div>
  );
}
